import { Component, OnInit, inject, signal } from '@angular/core';
import { DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { finalize, forkJoin } from 'rxjs';
import { SellerBusiness } from '../../core/models/product.models';
import { Business } from '../../core/models/user.models';
import { BusinessService } from '../../core/services/business.service';
import { SubscriptionPlan, SubscriptionPlansService, TenantSubscription } from '../../core/services/subscription-plans.service';
import { httpErrorMessage } from '../../core/utils/http-error-message';
import { CurrentUserService } from '../../core/auth/current-user.service';

type ReviewAction = 'Pending' | 'Approved' | 'CorrectionRequested' | 'OnHold' | 'Rejected' | 'Blacklisted';

@Component({ selector: 'app-business-review', imports: [FormsModule, RouterLink, DatePipe], templateUrl: './business-review.html', styleUrl: './business-review.scss' })
export class BusinessReview implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly businessesService = inject(BusinessService);
  private readonly plansService = inject(SubscriptionPlansService);
  private readonly currentUser = inject(CurrentUserService);
  readonly business = signal<SellerBusiness | null>(null);
  readonly profile = signal<Business | null>(null);
  readonly isLoading = signal(true);
  readonly isReviewing = signal(false);
  readonly errorMessage = signal('');
  readonly successMessage = signal('');
  readonly showAccountNumber = signal(false);
  readonly isManagingApproved = signal(false);
  readonly subscription = signal<TenantSubscription | null>(null);
  readonly availablePlans = signal<SubscriptionPlan[]>([]);
  readonly isChangingPlan = signal(false);
  readonly isSavingPlan = signal(false);
  reviewReason = '';
  selectedPlanId = '';
  can(permission: string): boolean { return this.currentUser.hasPermission(permission); }

  ngOnInit(): void {
    const tenantId = this.route.snapshot.paramMap.get('id');
    if (!tenantId) { this.errorMessage.set('Business request id is missing.'); this.isLoading.set(false); return; }
    forkJoin({
      businesses: this.businessesService.businesses(),
      profile: this.businessesService.adminProfileByTenant(tenantId),
      subscription: this.plansService.forTenant(tenantId),
      plans: this.plansService.list(),
    })
      .pipe(finalize(() => this.isLoading.set(false))).subscribe({
        next: ({ businesses, profile, subscription, plans }) => {
          this.business.set(businesses.find((item) => item.id === tenantId) ?? null);
          this.profile.set(profile);
          this.subscription.set(subscription);
          this.availablePlans.set(plans.filter((plan) => plan.isActive));
          this.selectedPlanId = subscription?.plan.id ?? '';
        },
        error: (error) => this.errorMessage.set(httpErrorMessage(error, 'Unable to load complete business application.')),
      });
  }

  review(status: ReviewAction): void {
    const business = this.business();
    if (!business) return;
    if ((status !== 'Approved' || business.approvalStatus === 'Blacklisted') && !this.reviewReason.trim()) { this.errorMessage.set('Enter a clear reason for this status change.'); return; }
    this.isReviewing.set(true); this.errorMessage.set(''); this.successMessage.set('');
    this.businessesService.reviewTenant(business.id, status, this.reviewReason.trim()).pipe(finalize(() => this.isReviewing.set(false))).subscribe({
      next: () => {
        this.business.set({ ...business, approvalStatus: status, approvalReason: this.reviewReason.trim() || null, reviewedAt: new Date().toISOString() });
        const profile = this.profile();
        if (profile) {
          this.profile.set({
            ...profile,
            onboardingStatus: status === 'Approved' ? 'Completed' : status,
            verificationStatus: status === 'Approved'
              ? 'Verified'
              : status === 'CorrectionRequested'
                ? 'CorrectionRequired'
                : status === 'Rejected' || status === 'Blacklisted'
                  ? 'Failed'
                  : 'Pending',
          });
        }
        this.successMessage.set(this.actionMessage(status)); this.reviewReason = '';
      },
      error: (error) => this.errorMessage.set(httpErrorMessage(error, 'Unable to complete business review.')),
    });
  }

  registeredAddress(profile: Business): string {
    const primary = profile.addressLine1?.trim() || '';
    const secondary = profile.addressLine2?.trim() || '';
    if (primary) return [secondary, primary].filter(Boolean).filter((value, index, values) => values.indexOf(value) === index).join(', ');
    return [secondary, profile.city, profile.state, profile.postalCode, profile.country].filter(Boolean).join(', ');
  }

  statusLabel(status?: string | null): string { return ({ Pending: 'Approval requested', CorrectionRequested: 'Correction requested', OnHold: 'On hold' } as Record<string, string>)[status || 'Pending'] || status || 'Approval requested'; }
  toggleAccountNumber(): void { this.showAccountNumber.update((value) => !value); }
  toggleApprovedManagement(): void { this.isManagingApproved.update((value) => !value); this.errorMessage.set(''); }
  togglePlanChange(): void {
    if (this.business()?.businessKind === 'PlatformOwned') return;
    this.selectedPlanId = this.subscription()?.plan.id ?? '';
    this.isChangingPlan.update((value) => !value);
    this.errorMessage.set('');
  }
  changePlan(): void {
    const business = this.business();
    if (!business || !this.selectedPlanId || this.selectedPlanId === this.subscription()?.plan.id) {
      this.errorMessage.set('Select a different active plan.');
      return;
    }
    const selected = this.availablePlans().find((plan) => plan.id === this.selectedPlanId);
    if (!selected) { this.errorMessage.set('The selected plan is not available.'); return; }
    this.isSavingPlan.set(true); this.errorMessage.set(''); this.successMessage.set('');
    this.plansService.assign(business.id, selected.id).pipe(finalize(() => this.isSavingPlan.set(false))).subscribe({
      next: () => {
        this.plansService.forTenant(business.id).subscribe({ next: (subscription) => this.subscription.set(subscription) });
        this.successMessage.set(`${business.businessName} is now on the ${selected.name} plan.`);
        this.isChangingPlan.set(false);
      },
      error: (error) => this.errorMessage.set(httpErrorMessage(error, 'Unable to change the business plan.')),
    });
  }
  private actionMessage(status: ReviewAction): string {
    return ({ Pending: 'Business moved back to pending review.', Approved: 'Business approved. The owner can now select a plan.', CorrectionRequested: 'Correction request sent to the business.', OnHold: 'Business application placed on hold.', Rejected: 'Business application rejected.', Blacklisted: 'Business blacklisted and access disabled.' } as Record<ReviewAction, string>)[status];
  }
}
